import { useState } from 'react';
import { X, AlertCircle } from 'lucide-react';

interface HoldPayoutModalProps {
  payoutId: string;
  payoutAmount: string;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (holdReason: string, internalNotes: string, notifyProvider: boolean) => void;
}

const HoldPayoutModal = ({
  payoutId,
  payoutAmount,
  isOpen,
  onClose,
  onConfirm,
}: HoldPayoutModalProps) => {
  const [holdReason, setHoldReason] = useState('');
  const [internalNotes, setInternalNotes] = useState('');
  const [notifyProvider, setNotifyProvider] = useState(false);

  const holdReasons = [
    'Pending Quality Assurance Review',
    'Customer Dispute Filed',
    'Compliance Documentation Missing',
    'Tax/Legal Hold',
    'Fraud Investigation',
    'Payment Method Verification',
    'Contract Violation Pending',
    'Manual Review Required',
    'Other',
  ];

  const handleConfirm = () => {
    if (holdReason) {
      onConfirm(holdReason, internalNotes, notifyProvider);
      handleClose();
    }
  };

  const handleClose = () => {
    setHoldReason('');
    setInternalNotes('');
    setNotifyProvider(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center">
              <AlertCircle className="w-5 h-5 text-yellow-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Hold Payout</h2>
              <p className="text-xs text-gray-600 mt-1">Temporarily restrict fund transfer for manual review.</p>
            </div>
          </div>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Payout Info */}
          <div className="grid grid-cols-2 gap-6 pb-6 border-b border-gray-200">
            <div>
              <p className="text-xs uppercase font-bold text-gray-500 tracking-wider mb-1">Payout ID</p>
              <p className="text-lg font-bold text-gray-900">{payoutId}</p>
            </div>
            <div>
              <p className="text-xs uppercase font-bold text-gray-500 tracking-wider mb-1">Payout Amount</p>
              <p className="text-lg font-bold text-gray-900">{payoutAmount}</p>
            </div>
          </div>

          {/* Hold Reason */}
          <div>
            <label className="block text-sm font-bold uppercase text-gray-500 tracking-wider mb-3">
              Hold Reason
            </label>
            <select
              value={holdReason}
              onChange={(e) => setHoldReason(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500 bg-white"
            >
              <option value="">Select a reason...</option>
              {holdReasons.map((reason) => (
                <option key={reason} value={reason}>
                  {reason}
                </option>
              ))}
            </select>
          </div>

          {/* Detailed Internal Notes */}
          <div>
            <label className="block text-xs uppercase font-bold text-gray-500 tracking-wider mb-2">
              Detailed Internal Notes
            </label>
            <textarea
              value={internalNotes}
              onChange={(e) => setInternalNotes(e.target.value)}
              placeholder="Provide internal context for the compliance team..."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500 resize-none"
              rows={5}
            />
          </div>

          {/* Notify Provider Toggle */}
          <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <input
              type="checkbox"
              id="notifyProvider"
              checked={notifyProvider}
              onChange={(e) => setNotifyProvider(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 mt-1"
            />
            <div className="flex-1">
              <label htmlFor="notifyProvider" className="block font-semibold text-gray-900 text-sm mb-1">
                Notify Provider
              </label>
              <p className="text-xs text-gray-600">Send automated hold notification via email</p>
            </div>
          </div>

          {/* Warning Banner */}
          <div className="flex items-start gap-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-bold text-yellow-700">Hold placed on payout funds</p>
              <p className="text-xs text-yellow-600 mt-1">Provider will not receive funds until hold is released by admin. Hold can be reviewed in Finance {' > '} Payout Holds.</p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 flex items-center justify-between gap-3">
          <button
            onClick={handleClose}
            className="px-6 py-2 text-gray-600 font-semibold hover:text-gray-800"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!holdReason}
            className="px-6 py-3 bg-yellow-500 hover:bg-yellow-600 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Confirm Hold
          </button>
        </div>
      </div>
    </div>
  );
};

export default HoldPayoutModal;
